import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  useBookingChecklists,
  useAutoCreateChecklists,
  useCreateBookingChecklist,
  useDeleteBookingChecklist,
  useUpdateServiceBook,
  useChecklistTemplates,
  type BookingChecklist,
} from '@/hooks/useChecklists';
import {
  useBookingInspections,
  useInspectionTemplates,
  useCreateInspectionFromTemplate,
  useDeleteInspection,
  type InspectionListItem,
} from '@/hooks/useInspections';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ChecklistFillDialog } from './ChecklistFillDialog';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { describeApiError } from '@/lib/describeApiError';
import { dateFnsLocale } from '@/utils/locale';

interface Props {
  workOrderId: string;
  serviceIds?: string[];
  serviceBookChecked?: boolean;
  serviceBookFilled?: boolean;
  serviceBookType?: string | null;
  finalInspectionCompleted?: boolean;
  finalInspectionCompletedAt?: string | null;
  cardVariant?: 'elevation' | 'outlined';
}

/**
 * Service-book checked status for create summary and the work-order card (#2339).
 * Unchecked is a warning, not a blocking error — create can still continue.
 */
export function ServiceBookCheckedStatus({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange?: (value: boolean) => void;
}) {
  const { t } = useTranslation('checklists');

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {checked ? (
        <Alert severity="success" data-testid="service-book-checked-alert">
          {t('serviceBook.checked')}
        </Alert>
      ) : (
        <Alert severity="warning" data-testid="service-book-unchecked-alert">
          <AlertTitle>{t('serviceBook.unchecked')}</AlertTitle>
          {t('serviceBook.uncheckedHint')}
        </Alert>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => onCheckedChange?.(e.target.checked)}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2">{t('serviceBook.checked')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('serviceBook.checkedHint')}
            </Typography>
          </Box>
        }
      />
    </Stack>
  );
}

export function ServiceBookChecklistsCard({
  workOrderId,
  serviceIds = [],
  serviceBookChecked = false,
  serviceBookFilled = false,
  serviceBookType = null,
  finalInspectionCompleted = false,
  finalInspectionCompletedAt = null,
  cardVariant,
}: Props) {
  const { t } = useTranslation('checklists');
  const { t: ti } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const { t: ts } = useTranslation('settings');
  const navigate = useNavigate();
  const notify = useNotify();
  const user = useAuthStore((s) => s.user);
  const canDeleteInspection =
    user?.role === 'developer' || (user?.permissions ?? []).includes('inspections.delete');
  const { serviceBookLinks } = useTenantSettings();

  const { data: checklists = [] } = useBookingChecklists(workOrderId);
  const { data: templates = [] } = useChecklistTemplates();
  const autoCreate = useAutoCreateChecklists();
  const createChecklist = useCreateBookingChecklist();
  const deleteChecklist = useDeleteBookingChecklist();
  const updateServiceBook = useUpdateServiceBook();

  // Inspection templates & inspections — errors are silently ignored to prevent page crash
  const { data: inspectionTemplatesData, isError: inspTmplError } = useInspectionTemplates();
  const inspectionTemplates = inspTmplError ? [] : (inspectionTemplatesData?.data ?? []);
  const { data: inspectionsRaw, isError: inspError } = useBookingInspections(workOrderId);
  const inspections = inspError ? [] : (inspectionsRaw ?? []);
  const createInspectionFromTemplate = useCreateInspectionFromTemplate();
  const deleteInspection = useDeleteInspection();

  // Count matching templates (default or service-linked), excluding already created
  const existingTemplateIds = useMemo(
    () => new Set(checklists.map((cl) => cl.templateId).filter(Boolean)),
    [checklists]
  );
  const matchingTemplateCount = useMemo(() => {
    return templates.filter((tmpl) => {
      if (existingTemplateIds.has(tmpl.id)) return false;
      if (tmpl.isDefault) return true;
      if (tmpl.serviceIds && tmpl.serviceIds.length > 0) {
        return tmpl.serviceIds.some((sid) => serviceIds.includes(sid));
      }
      return false;
    }).length;
  }, [templates, serviceIds, existingTemplateIds]);

  const [fillChecklistId, setFillChecklistId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BookingChecklist | null>(null);
  const [showAddSelect, setShowAddSelect] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Inspection state
  const [showInspectionAdd, setShowInspectionAdd] = useState(false);
  const [selectedInspectionTemplateId, setSelectedInspectionTemplateId] = useState('');
  const [deleteInspectionTarget, setDeleteInspectionTarget] = useState<InspectionListItem | null>(null);

  const navigateToInspection = (inspectionId: string) => {
    navigateAwayFromWorkOrderDetail(window.location.pathname, `/inspections/${inspectionId}`, navigate);
  };
  const handleServiceBookToggle = async (field: 'serviceBookChecked' | 'serviceBookFilled' | 'finalInspectionCompleted', value: boolean) => {
    try {
      await updateServiceBook.mutateAsync({ workOrderId, [field]: value });
      notify.success(t('serviceBook.updated'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.serviceBookFailed') }));
    }
  };

  const handleServiceBookType = async (value: string) => {
    try {
      await updateServiceBook.mutateAsync({
        workOrderId,
        serviceBookType: (value || null) as 'digital' | 'paper' | 'both' | null,
      });
      notify.success(t('serviceBook.updated'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.serviceBookFailed') }));
    }
  };

  const handleAutoCreate = async () => {
    try {
      const result = await autoCreate.mutateAsync(workOrderId);
      if (result.count > 0) {
        notify.success(t('booking.autoCreated', { count: result.count }));
      } else {
        notify.info(t('booking.autoCreatedNone'));
      }
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.autoCreateFailed') }));
    }
  };

  const handleAddChecklist = async () => {
    if (!selectedTemplateId) return;
    try {
      await createChecklist.mutateAsync({
        bookingId: workOrderId,
        templateId: selectedTemplateId,
      });
      setShowAddSelect(false);
      setSelectedTemplateId('');
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.createFailed') }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteChecklist.mutateAsync({ id: deleteTarget.id, bookingId: workOrderId });
      notify.success(t('booking.deleted'));
      setDeleteTarget(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.deleteFailed') }));
    }
  };

  const handleAddInspection = async () => {
    if (!selectedInspectionTemplateId) return;
    try {
      await createInspectionFromTemplate.mutateAsync({
        bookingId: workOrderId,
        templateId: selectedInspectionTemplateId,
      });
      setShowInspectionAdd(false);
      setSelectedInspectionTemplateId('');
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.inspectionCreateFailed') }));
    }
  };

  const handleDeleteInspection = async () => {
    if (!deleteInspectionTarget) return;
    try {
      await deleteInspection.mutateAsync(deleteInspectionTarget.id);
      setDeleteInspectionTarget(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.inspectionDeleteFailed') }));
    }
  };

  const inspectionStatusColor: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    draft: 'default',
    pending: 'default',
    in_progress: 'info',
    completed: 'success',
    sent_to_customer: 'warning',
    approved: 'success',
    rejected: 'error',
  };

  const inspectionStatusLabel: Record<string, string> = {
    draft: ti('status.draft'),
    pending: ti('status.pending'),
    in_progress: ti('status.in_progress'),
    completed: ti('status.completed'),
    sent_to_customer: ti('status.sent_to_customer'),
    approved: ti('status.approved'),
    rejected: ti('status.rejected'),
  };

  return (
    <>
      <Card variant={cardVariant} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PlaylistAddCheckIcon color="primary" />
            <Typography variant="h6">{t('booking.title')}</Typography>
          </Box>

          {/* Service Book Section */}
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {t('serviceBook.title')}
          </Typography>
          <Stack spacing={0.5} mb={2}>
            <ServiceBookCheckedStatus
              checked={serviceBookChecked}
              onCheckedChange={(value) => void handleServiceBookToggle('serviceBookChecked', value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={serviceBookFilled}
                  onChange={(e) => void handleServiceBookToggle('serviceBookFilled', e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('serviceBook.filled')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('serviceBook.filledHint')}
                  </Typography>
                </Box>
              }
            />
            <TextField
              select
              label={t('serviceBook.type')}
              value={serviceBookType ?? ''}
              onChange={(e) => void handleServiceBookType(e.target.value)}
              size="small"
              fullWidth
              sx={{ mt: 1 }}
            >
              <MenuItem value="">&mdash;</MenuItem>
              <MenuItem value="digital">{t('serviceBook.types.digital')}</MenuItem>
              <MenuItem value="paper">{t('serviceBook.types.paper')}</MenuItem>
              <MenuItem value="both">{t('serviceBook.types.both')}</MenuItem>
            </TextField>
          </Stack>

          {serviceBookLinks.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {ts('serviceBookLinks.title')}
              </Typography>
              <Stack spacing={0.75}>
                {serviceBookLinks.map((link, index) => (
                  <Box key={`${link.url}-${String(index)}`}>
                    <Link href={link.url} target="_blank" rel="noopener" underline="hover">
                      {link.name}
                    </Link>
                    {link.note !== undefined && link.note !== '' && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {link.note}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
          {/* Inspection Reports Section */}
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {ti('reportsTitle')}
          </Typography>

          {inspections.length === 0 && !showInspectionAdd && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {ti('noInspections')}
            </Typography>
          )}

          <Stack spacing={1} mb={1.5}>
            {inspections.map((insp) => {
              const isCompleted = insp.status === 'completed' || insp.status === 'approved';
              return (
                <Box
                  key={insp.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isCompleted ? 'success.200' : 'divider',
                    bgcolor: isCompleted ? 'success.50' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isCompleted ? 'success.100' : 'action.hover' },
                  }}
                  onClick={() => navigateToInspection(insp.id)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {insp.templateId
                        ? inspectionTemplates.find((t2) => t2.id === insp.templateId)?.name ?? ti('inspection')
                        : ti('inspection')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        size="small"
                        label={inspectionStatusLabel[insp.status] ?? insp.status}
                        color={inspectionStatusColor[insp.status] ?? 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      {canDeleteInspection && (
                        <Tooltip title={tc('delete')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteInspectionTarget(insp);
                            }}
                            aria-label={tc('delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  {insp.totalIssues > 0 && (
                    <Typography variant="caption" color="error.main">
                      {ti('dashboard.issuesFound', { count: insp.totalIssues })}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>

          {/* Add inspection from template */}
          {showInspectionAdd && (
            <Box display="flex" gap={1} mb={1}>
              <TextField
                select
                label={ti('selectTemplate')}
                value={selectedInspectionTemplateId}
                onChange={(e) => setSelectedInspectionTemplateId(e.target.value)}
                size="small"
                fullWidth
              >
                {inspectionTemplates.map((tmpl) => (
                  <MenuItem key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                size="small"
                onClick={() => void handleAddInspection()}
                disabled={!selectedInspectionTemplateId || createInspectionFromTemplate.isPending}
              >
                {tc('add')}
              </Button>
              <Button size="small" onClick={() => { setShowInspectionAdd(false); setSelectedInspectionTemplateId(''); }}>
                {tc('cancel')}
              </Button>
            </Box>
          )}

          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowInspectionAdd(true)}
              disabled={showInspectionAdd}
            >
              {ti('addInspection')}
            </Button>
          </Box>

          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
          {/* Checklists Section */}
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {t('title')}
          </Typography>

          {checklists.length === 0 && !showAddSelect && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t('booking.noChecklists')}
            </Typography>
          )}

          <Stack spacing={1} mb={1.5}>
            {checklists.map((cl) => {
              const progress = cl.totalItems > 0
                ? Math.round((cl.completedCount / cl.totalItems) * 100)
                : 0;
              const isCompleted = cl.status === 'completed';

              return (
                <Box
                  key={cl.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isCompleted ? 'success.200' : 'divider',
                    bgcolor: isCompleted ? 'success.50' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isCompleted ? 'success.100' : 'action.hover' },
                  }}
                  onClick={() => setFillChecklistId(cl.id)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {cl.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        size="small"
                        label={t(`status.${cl.status}`)}
                        color={isCompleted ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Tooltip title={tc('delete')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(cl);
                          }}
                          aria-label={tc('delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      color={isCompleted ? 'success' : 'primary'}
                      sx={{ flex: 1, borderRadius: 1, height: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                      {cl.completedCount}/{cl.totalItems}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* Add checklist from template */}
          {showAddSelect && (
            <Box display="flex" gap={1} mb={1}>
              <TextField
                select
                label={t('booking.selectTemplate')}
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                size="small"
                fullWidth
              >
                {templates.map((tmpl) => (
                  <MenuItem key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                size="small"
                onClick={() => void handleAddChecklist()}
                disabled={!selectedTemplateId || createChecklist.isPending}
              >
                {tc('add')}
              </Button>
              <Button size="small" onClick={() => { setShowAddSelect(false); setSelectedTemplateId(''); }}>
                {tc('cancel')}
              </Button>
            </Box>
          )}

          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowAddSelect(true)}
              disabled={showAddSelect}
            >
              {t('booking.addChecklist')}
            </Button>
            {matchingTemplateCount > 0 && (
              <Tooltip title={t('booking.autoCreateHint', { count: matchingTemplateCount })}>
                <Button
                  size="small"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => void handleAutoCreate()}
                  disabled={autoCreate.isPending}
                >
                  {t('booking.autoCreateFromTemplates', { count: matchingTemplateCount })}
                </Button>
              </Tooltip>
            )}
          </Box>

          </Grid>
          </Grid>

          {/* Final Inspection Section — after all work is done */}
          <Typography variant="subtitle2" color="text.secondary" mb={1} mt={2}>
            {t('finalInspection.completed')}
          </Typography>
          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={finalInspectionCompleted}
                  onChange={(e) => void handleServiceBookToggle('finalInspectionCompleted', e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('finalInspection.completed')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('finalInspection.completedHint')}
                  </Typography>
                  {finalInspectionCompleted && finalInspectionCompletedAt && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {t('finalInspection.completedAt', {
                        date: format(new Date(finalInspectionCompletedAt), 'dd.MM.yyyy', { locale: dateFnsLocale() }),
                        time: format(new Date(finalInspectionCompletedAt), 'HH:mm', { locale: dateFnsLocale() }),
                      })}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Fill dialog — mechanic-optimized one-tap rows */}
      <ChecklistFillDialog
        checklistId={fillChecklistId}
        onClose={() => setFillChecklistId(null)}
        mechanicMode
      />

      {/* Delete checklist confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        title={tc('delete')}
        message={t('booking.deleteConfirm', { name: deleteTarget?.name ?? '' })}
      />

      {/* Delete inspection confirmation */}
      <ConfirmDialog
        open={!!deleteInspectionTarget}
        onCancel={() => setDeleteInspectionTarget(null)}
        onConfirm={() => void handleDeleteInspection()}
        title={tc('delete')}
        message={ti('confirmDelete')}
      />
    </>
  );
}
